import { DataVersion } from '@hakimo-ui/hakimo/types';
import {
  toast,
  useFileDownload,
  withDataVersion,
} from '@hakimo-ui/hakimo/util';
import useAuthenticatedMutation from '../shared/useAuthenticatedMutation';

function withPartialSuffix(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1
    ? `${fileName}_partial`
    : `${fileName.slice(0, dot)}_partial${fileName.slice(dot)}`;
}

export function useDownloadScanEventMedia(
  eventId: string,
  onSuccess?: () => void,
  onError?: (err: Error) => void,
  dataVersion?: DataVersion
) {
  const { downloadFile } = useFileDownload();
  const url = `/v2/orm/alarm/media/${eventId}/download`;

  const request = new Request(url, {
    method: 'GET',
  });
  withDataVersion(request, dataVersion);

  return useAuthenticatedMutation<null, void>(request, {
    onSuccess,
    onError,
    getBody: () => null,
    responseModifier: async (response) => {
      const blob = await response.blob();
      // A short clip looks identical to an event that genuinely had little
      // footage. Zero encoded frames is the emptiest case, not an exempt
      // one, so it is not excluded here.
      const available = Number(response.headers.get('X-Frames-Available'));
      const encoded = Number(response.headers.get('X-Frames-Encoded'));
      const isPartial = available > 0 && encoded < available;
      const fileName =
        response.headers.get('X-File-Name') || `scan_event_${eventId}.mp4`;
      downloadFile({
        blob,
        // The toast is gone in three seconds; the file gets filed as
        // evidence. Record partialness where it survives, as C5 does for
        // debug renders.
        fileName: isPartial ? withPartialSuffix(fileName) : fileName,
      });
      if (isPartial) {
        toast(
          `Downloaded a partial clip: ${encoded} of ${available} frames. Some footage is missing.`,
          { type: 'warning' }
        );
      }
      return null;
    },
  });
}

export default useDownloadScanEventMedia;
